import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { filesApi } from '@/lib/api';
import type { Attachment } from '@/types';

export function useAttachments(taskId: string | undefined) {
  return useQuery({
    queryKey: ['attachments', taskId],
    queryFn: async () => {
      if (!taskId) throw new Error('Task ID required');
      const response = await filesApi.getByTask(taskId);
      return response.data as Attachment[];
    },
    enabled: !!taskId,
  });
}

export function useUploadAttachment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskId, file }: { taskId: string; file: File }) => {
      const response = await filesApi.uploadToTask(taskId, file);
      return response.data as Attachment;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['attachments', variables.taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', variables.taskId] });
    },
  });
}

export function useDeleteAttachment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: string; taskId: string }) => {
      await filesApi.delete(id);
    },
    onSuccess: (_, variables) => {
      queryClient.setQueryData<Attachment[]>(['attachments', variables.taskId], (old) => {
        if (!old) return old;
        return old.filter((a) => a.id !== variables.id);
      });
      queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', variables.taskId] });
    },
  });
}
